import type * as Blockly from 'blockly/core';
import type { GpsDistanceData } from '@/stores/useBlockModalGpsDistanceStore';
import { BOARD_CONFIG } from '@/(routers)/boards/editor/block/blockConfig';
import {
    expr,
    registerHelperFunction,
    registerSetupCode,
    resolveBoard,
} from '@/(routers)/boards/editor/block/generators/arduino/_gen';
import {
    GPS_BASE_HELPER_CODE,
    GPS_BASE_HELPER_CODE_BLUE,
    GPS_POSITION_LABELS,
    gpsGlobalCode,
    gpsGlobalCodeBlue,
    gpsGlobalCodes,
    markGpsUsed,
    resetGps,
} from '@/(routers)/boards/editor/block/generators/arduino/blocks/gps/helper/gps_helper';

export { resetGps as resetGpsDistance };

type Position = keyof (typeof BOARD_CONFIG)[keyof typeof BOARD_CONFIG]['pins'];

const DISTANCE_HELPER_CODE = `float _sbeGpsDistance(TinyGPSPlus* gps, SoftwareSerial* serial, float targetLat, float targetLng, bool* warned, const char* label) {
  _sbeGpsCheckAge(gps, serial, warned, label);
  if (!gps->location.isValid()) return 0.0;
  return TinyGPSPlus::distanceBetween(
    gps->location.lat(), gps->location.lng(),
    targetLat, targetLng
  );
}`;

const DISTANCE_HELPER_CODE_BLUE = `float _sbeGpsDistance_blue(TinyGPSPlus* gps, float targetLat, float targetLng, bool* warned, const char* label) {
  _sbeGpsCheckAge_blue(gps, warned, label);
  if (!gps->location.isValid()) return 0.0;
  return TinyGPSPlus::distanceBetween(
    gps->location.lat(), gps->location.lng(),
    targetLat, targetLng
  );
}`;

export function register(gen: any): void {
    gen.forBlock['gps_distance'] = function (block: Blockly.Block): [string, number] {
        const position = block.getFieldValue('POSITION') as Position;
        const locationJson = block.getFieldValue('LOCATION') as string;
        const unit = block.getFieldValue('UNIT') as string;
        const board = resolveBoard(block);
        const pins = BOARD_CONFIG[board].pins[position];
        const rx = pins.second;
        const tx = pins.first;
        const label = GPS_POSITION_LABELS[position] ?? position;

        let location: GpsDistanceData | null = null;
        try {
            if (locationJson) location = JSON.parse(locationJson);
        } catch {}

        if (!location || !Number.isFinite(location.lat) || !Number.isFinite(location.lng)) return expr('0');

        markGpsUsed();

        if (board === 'blue') {
            gpsGlobalCodes.set(`${tx}`, gpsGlobalCodeBlue(tx));
            registerHelperFunction('gps_base', GPS_BASE_HELPER_CODE_BLUE);
            registerHelperFunction('gps_distance_fn', DISTANCE_HELPER_CODE_BLUE);
            registerSetupCode(
                `gps_${tx}`,
                `  Serial2.begin(9600, SERIAL_8N1, ${rx}, ${tx});\n  _sbeGpsBegin_blue("${label}");\n`,
            );
            const distCode = `_sbeGpsDistance_blue(&_sbeGps_${tx}, ${location.lat.toFixed(6)}, ${location.lng.toFixed(
                6,
            )}, &_sbeGpsWarned_${tx}, "${label}")`;
            return expr(unit === 'KM' ? `(${distCode} / 1000.0)` : distCode);
        }

        gpsGlobalCodes.set(`${tx}`, gpsGlobalCode(rx, tx));
        registerHelperFunction('gps_base', GPS_BASE_HELPER_CODE);
        registerHelperFunction('gps_distance_fn', DISTANCE_HELPER_CODE);
        registerSetupCode(`gps_${tx}`, `  _sbeGpsBegin(&_sbeGpsSerial_${tx}, "${label}");\n`);
        const distCode = `_sbeGpsDistance(&_sbeGps_${tx}, &_sbeGpsSerial_${tx}, ${location.lat.toFixed(
            6,
        )}, ${location.lng.toFixed(6)}, &_sbeGpsWarned_${tx}, "${label}")`;
        return expr(unit === 'KM' ? `(${distCode} / 1000.0)` : distCode);
    };
}
